import { useOrderDesignVisualization } from "@jasper/use-cases";
import { Alert, Box, Flex, Loader, Modal } from "@mantine/core";
import { useEffect, useState } from "react";
import { ModelVisualizer } from "@jasper/model-visualizer";
import { OrderDesignStatusEnum } from "@jasper/client-models";

interface Props {
  orderDesignId: string;
  interactionsEnabled?: boolean;
  borderRadius?: string | number;
  bottomOverlay?: {
    render: ({
      fullscreen,
      openFullscreenModal,
    }: {
      fullscreen: boolean;
      openFullscreenModal: () => void;
    }) => React.ReactElement;
  };
  topOverlay?: {
    render: ({
      fullscreen,
      openFullscreenModal,
    }: {
      fullscreen: boolean;
      openFullscreenModal: () => void;
    }) => React.ReactElement;
  };
  isFullscreen?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

export const OrderDesignPreview = ({
  orderDesignId,
  topOverlay,
  bottomOverlay,
  interactionsEnabled = true,
  borderRadius = "5px",
  isFullscreen,
  onFullscreenChange,
}: Props) => {
  const [isInternalFullscreen, setIsInternalFullscreen] = useState(false);
  const isModalOpen = isFullscreen ?? isInternalFullscreen;
  const setIsModalOpen = onFullscreenChange ?? setIsInternalFullscreen;
  const [orderDesignFiles, setOrderDesignFiles] = useState<string[]>([]);
  const [isFetchingOrderDesignFiles, setIsFetchingOrderDesignFiles] =
    useState(false);

  const { orderDesign, isLoading, error, getOrderDesignFiles } =
    useOrderDesignVisualization({
      orderDesignId,
    });

  useEffect(() => {
    if (
      isLoading ||
      error ||
      !orderDesign ||
      orderDesign.filenames.length === 0 ||
      !getOrderDesignFiles
    ) {
      return;
    }

    setIsFetchingOrderDesignFiles(true);

    // For now we only handle design with single file
    const filename = orderDesign.filenames[0];

    getOrderDesignFiles()
      .then(files => {
        if (files.length > 0) {
          setOrderDesignFiles(files);
        }
      })
      .finally(() => {
        setIsFetchingOrderDesignFiles(false);
      });
  }, [orderDesign, isLoading, error, getOrderDesignFiles]);

  if (isLoading || isFetchingOrderDesignFiles) {
    return (
      <Flex justify="center" align="center" h="100%">
        <Loader />
      </Flex>
    );
  }

  if (
    !error &&
    orderDesign &&
    (orderDesign.status === OrderDesignStatusEnum.REQUESTED ||
      orderDesign.status === OrderDesignStatusEnum.DRAFT)
  ) {
    return (
      <Flex justify="center" align="center" h="100%" p="md">
        <Alert color="blue">
          This design is in "{orderDesign.status}" status and is not yet
          available for visualization.
        </Alert>
      </Flex>
    );
  }

  if (error || !orderDesignFiles.length) {
    return (
      <Flex justify="center" align="center" h="100%">
        <Alert color="red">
          Une erreur est survenue lors du chargement de la conception de votre
          prothèse. Veuillez réessayer plus tard.
        </Alert>
      </Flex>
    );
  }

  const openFullscreenModal = () => setIsModalOpen(true);

  return (
    <>
      <Modal
        size="100%"
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <Box h="80vh">
          <ModelVisualizer
            stls={orderDesignFiles.map((url, i) => ({
              url,
              name: `Vue ${i + 1}`,
            }))}
            interactionsEnabled={interactionsEnabled}
            topOverlay={
              topOverlay
                ? topOverlay.render({ fullscreen: true, openFullscreenModal })
                : undefined
            }
            bottomOverlay={
              bottomOverlay
                ? bottomOverlay.render({
                    fullscreen: true,
                    openFullscreenModal,
                  })
                : undefined
            }
          />
        </Box>
      </Modal>

      {/* Prevent having multiple visualizer loaded */}
      {!isModalOpen && (
        <ModelVisualizer
          stls={orderDesignFiles.map((url, i) => ({
            url,
            name: `Vue ${i + 1}`,
          }))}
          style={{ borderRadius }}
          interactionsEnabled={interactionsEnabled}
          topOverlay={
            topOverlay
              ? topOverlay.render({ fullscreen: false, openFullscreenModal })
              : undefined
          }
          bottomOverlay={
            bottomOverlay
              ? bottomOverlay.render({ fullscreen: false, openFullscreenModal })
              : undefined
          }
        />
      )}
    </>
  );
};
